import * as React from 'react';

import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';

import Close from '@/icons/close.svg';
import { cn } from '@/utils';

import Button from './Button';
import VisuallyHidden from './VisuallyHidden';

const Sheet = SheetPrimitive.Root;
const SheetTrigger = SheetPrimitive.Trigger;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
	<SheetPrimitive.Overlay
		className={cn(
			'fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
			className
		)}
		{...props}
		ref={ref}
	/>
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const SHEET_VARIANTS = {
	FADE: 'fade',
	TOP: 'top',
	RIGHT: 'right',
	BOTTOM: 'bottom',
	LEFT: 'left'
};

const sheetVariants = cva(
	'shadow-sidebar fixed z-50 gap-4 bg-background outline-none transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out',
	{
		variants: {
			appear: {
				[SHEET_VARIANTS.FADE]:
					'inset-x-0 top-0 border-b data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
				[SHEET_VARIANTS.TOP]:
					'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
				[SHEET_VARIANTS.BOTTOM]:
					'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
				[SHEET_VARIANTS.LEFT]:
					'inset-y-0 left-0 h-full w-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
				[SHEET_VARIANTS.RIGHT]:
					'inset-y-0 right-0 h-full w-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right'
			}
		},
		defaultVariants: {
			appear: SHEET_VARIANTS.BOTTOM
		}
	}
);

const SheetContent = React.forwardRef(
	(
		{
			appear = SHEET_VARIANTS.BOTTOM,
			title = 'Menu',
			className,
			children,
			hasCross = true,
			closeMenu,
			...props
		},
		ref
	) => {
		const { t } = useTranslation();

		return (
			<SheetPortal>
				<SheetOverlay />
				<SheetPrimitive.Content
					ref={ref}
					className={cn(sheetVariants({ appear }), className)}
					{...props}
				>
					<VisuallyHidden>
						<SheetPrimitive.Title>{title}</SheetPrimitive.Title>
					</VisuallyHidden>
					<SheetPrimitive.Description asChild>
						<div className="w-full">{children}</div>
					</SheetPrimitive.Description>
					{hasCross && (
						<Button
							variant="ghost"
							size="icon"
							asChild
							className="absolute right-4 top-3 focus:outline-none"
						>
							<SheetPrimitive.Close>
								<Close className="size-6 text-white hover:text-black" />
								<span className="sr-only">{t('common.close')}</span>
							</SheetPrimitive.Close>
						</Button>
					)}
				</SheetPrimitive.Content>
			</SheetPortal>
		);
	}
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

export {
	SHEET_VARIANTS,
	Sheet,
	SheetPortal,
	SheetOverlay,
	SheetContent,
	SheetTrigger
};
